// Where the documentation lives and how Decode proves sign-in to it.
//
// The docs are a Docusaurus site served under /docs on this same origin by the
// serve_docs Cloud Function (InsuranceBackend cloud_functions/platform/serve_docs.py),
// which checks the Site Firebase ID token in the `__session` cookie on every
// request. `__session` is the only cookie Firebase Hosting forwards to a rewrite.
//
// scripts/build-env.js pins REACT_APP_DOCS_BASE_URL to '/docs' for every deployed
// build; a developer may point it at a local `docusaurus start` server via .env.local.
export const DOCS_BASE = process.env.REACT_APP_DOCS_BASE_URL || '/docs';

export function docsUrl(path = '') {
  const clean = path.replace(/^\/+/, '');
  return clean ? `${DOCS_BASE}/${clean}` : `${DOCS_BASE}/`;
}

const COOKIE_NAME = '__session';
const COOKIE_PATH = '/docs';   // never sent with the app's own asset requests
const REFRESH_LEAD_MS = 5 * 60 * 1000;

// Mirror the SDK's current Site ID token so the browser attaches it to docs
// navigations. Expires with the token; the SDK's next refresh rewrites it.
export function setDocsSessionCookie(token, expirationTime) {
  const expires = new Date(expirationTime).toUTCString();
  document.cookie = `${COOKIE_NAME}=${token}; Path=${COOKIE_PATH}; Expires=${expires}; Secure; SameSite=Lax`;
}

export function clearDocsSessionCookie() {
  document.cookie = `${COOKIE_NAME}=; Path=${COOKIE_PATH}; Max-Age=0; Secure; SameSite=Lax`;
}

// The Site token is refreshed lazily (when an API call needs it), so an idle tab
// would let the cookie expire and the next docs click would bounce through /.
// Refresh five minutes early instead; the delay is measured from the token's
// own expiry so it is right whether the token is fresh or already old.
export function docsCookieRefreshDelay(expirationTime) {
  return Math.max(new Date(expirationTime).getTime() - Date.now() - REFRESH_LEAD_MS, 0);
}

// serve_docs sends an unauthenticated page request to /?docs=<docs path>. Only
// a same-origin path under /docs is honoured, so this can never redirect off-site.
// The browser carries the #anchor across the 302; re-attach it.
export function consumeDocsReturnPath() {
  const target = new URLSearchParams(window.location.search).get('docs');
  forgetDocsReturnPath();
  if (!target || !/^\/docs(\/|$)/.test(target)) return null;
  return target + window.location.hash;
}

// Drops the ?docs= parameter so a later token refresh does not try the
// hand-off again.
export function forgetDocsReturnPath() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('docs')) return;
  url.searchParams.delete('docs');
  window.history.replaceState(window.history.state, '', url);
}

// Asks serve_docs whether it can see the cookie this app just wrote. A
// same-origin request carries the /docs-scoped cookie exactly as a docs
// navigation would, and serve_docs answers 200 only for a valid Site token
// with Decode access; with no usable cookie a page request is a redirect,
// which fetch reports as an opaque redirect when told not to follow it. This
// is what stops the bounce loop: the app goes back to the docs only once the
// server has confirmed the cookie was accepted. Rejects on a network failure.
export async function docsCookieAccepted() {
  const response = await fetch(docsUrl(), {
    method: 'HEAD',
    redirect: 'manual',
    cache: 'no-store',
    credentials: 'same-origin',
  });
  return response.ok;
}
